import React, { useState, useEffect, useMemo } from 'react';
import { adminService } from '../../services/adminService';
import './PaymentOverview.css';

interface Payment {
  id: string;
  bookingId: string;
  bookingReference: string;
  customerName: string;
  customerEmail: string;
  amount: number;
  status: 'pending' | 'paid' | 'partial' | 'refunded' | 'failed';
  method: 'stripe' | 'paypal' | 'transfer' | 'cash' | 'invoice';
  paidAt?: string;
  dueDate: string;
  invoiceNumber: string;
  description: string;
  deposit?: number;
  remainingAmount?: number;
  tourDate: string;
  participants: number;
  notes?: string;
  transactionId?: string;
  refundedAmount?: number;
  refundedAt?: string;
}

interface PaymentStats {
  totalRevenue: number;
  todayRevenue: number;
  weekRevenue: number;
  monthRevenue: number;
  pendingPayments: number;
  overduePayments: number;
  refundedAmount: number;
  successRate: number;
}

const PaymentOverview: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterMethod, setFilterMethod] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'dueDate',
    direction: 'desc'
  });
  // Mock-Daten für Entwicklung
  const mockPayments: Payment[] = [
    {
      id: '1',
      bookingId: 'BK-2025-001',
      bookingReference: 'FF-2025-001',
      customerName: 'Max Mustermann',
      customerEmail: 'max@example.com',
      amount: 1250.00,
      status: 'paid',
      method: 'stripe',
      paidAt: '2025-09-14T10:30:00',
      dueDate: '2025-09-20',
      invoiceNumber: 'INV-2025-001',
      description: 'Floßfahrt Premium - 25 Personen',
      tourDate: '2025-09-28',
      participants: 25,
      transactionId: 'ch_1234567890'
    },
    {
      id: '2',
      bookingId: 'BK-2025-002',
      bookingReference: 'FF-2025-002',
      customerName: 'Anna Schmidt',
      customerEmail: 'anna@example.com',
      amount: 890.00,
      status: 'partial',
      method: 'transfer',
      deposit: 178.00,
      remainingAmount: 712.00,      dueDate: '2025-09-25',
      invoiceNumber: 'INV-2025-002',
      description: 'Floßfahrt Standard - 18 Personen',
      tourDate: '2025-10-05',
      participants: 18,
      notes: 'Anzahlung erhalten'
    },
    {
      id: '3',
      bookingId: 'BK-2025-003',
      bookingReference: 'FF-2025-003',
      customerName: 'Firma ABC GmbH',
      customerEmail: 'buchhaltung@abc.de',
      amount: 2450.00,
      status: 'pending',
      method: 'invoice',
      dueDate: '2025-09-18',
      invoiceNumber: 'INV-2025-003',
      description: 'Betriebsausflug - 45 Personen + Catering',
      tourDate: '2025-10-12',
      participants: 45,
      notes: 'Rechnung versendet am 04.09.2025'
    },
    {
      id: '4',
      bookingId: 'BK-2025-004',
      bookingReference: 'FF-2025-004',
      customerName: 'Thomas Müller',
      customerEmail: 'thomas@example.com',
      amount: 650.00,      status: 'refunded',
      method: 'paypal',
      paidAt: '2025-09-10T14:20:00',
      refundedAt: '2025-09-12T09:15:00',
      refundedAmount: 650.00,
      dueDate: '2025-09-15',
      invoiceNumber: 'INV-2025-004',
      description: 'Floßfahrt Standard - 12 Personen',
      tourDate: '2025-09-20',
      participants: 12,
      notes: 'Stornierung wegen Krankheit'
    },
    {
      id: '5',
      bookingId: 'BK-2025-005',
      bookingReference: 'FF-2025-005',
      customerName: 'Sarah Weber',
      customerEmail: 'sarah@example.com',
      amount: 1850.00,
      status: 'paid',
      method: 'cash',
      paidAt: '2025-09-13T16:45:00',
      dueDate: '2025-09-22',
      invoiceNumber: 'INV-2025-005',
      description: 'Hochzeitsfeier - 35 Personen',
      tourDate: '2025-09-30',
      participants: 35
    }
  ];
  // Stats berechnen
  const stats: PaymentStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const paidPayments = payments.filter(p => p.status === 'paid' || p.status === 'partial');
    
    const todayRevenue = paidPayments
      .filter(p => p.paidAt && new Date(p.paidAt) >= today)
      .reduce((sum, p) => sum + (p.deposit || p.amount), 0);
    
    const weekRevenue = paidPayments
      .filter(p => p.paidAt && new Date(p.paidAt) >= weekAgo)
      .reduce((sum, p) => sum + (p.deposit || p.amount), 0);
    
    const monthRevenue = paidPayments
      .filter(p => p.paidAt && new Date(p.paidAt) >= monthAgo)
      .reduce((sum, p) => sum + (p.deposit || p.amount), 0);

    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const overduePayments = payments.filter(p => 
      p.status === 'pending' && new Date(p.dueDate) < today
    ).length;
    
    const refundedAmount = payments
      .filter(p => p.status === 'refunded')
      .reduce((sum, p) => sum + (p.refundedAmount || 0), 0);
    const totalPayments = payments.length;
    const successfulPayments = payments.filter(p => p.status === 'paid').length;
    const successRate = totalPayments > 0 ? (successfulPayments / totalPayments) * 100 : 0;

    return {
      totalRevenue: paidPayments.reduce((sum, p) => sum + (p.deposit || p.amount), 0),
      todayRevenue,
      weekRevenue,
      monthRevenue,
      pendingPayments,
      overduePayments,
      refundedAmount,
      successRate
    };
  }, [payments]);

  useEffect(() => {
    // Load real data from API
    const fetchPayments = async () => {
      try {
        const response = await adminService.getPayments({
          page: 1,
          pageSize: 100
        });
        
        // Transform API data to component format
        const transformedPayments: Payment[] = response.payments.map((p: any) => ({
          id: p.id,
          bookingId: p.bookingId,
          bookingReference: p.bookingReference || p.bookingId,
          customerName: p.customerName,
          customerEmail: p.customerEmail || '',
          amount: p.amount,
          status: p.status.toLowerCase(),
          method: p.method.toLowerCase() as any,
          paidAt: p.paidAt,
          dueDate: p.dueDate,
          invoiceNumber: p.invoiceNumber,
          description: p.description,
          deposit: p.deposit,
          remainingAmount: p.remainingAmount,
          tourDate: p.tourDate,
          participants: p.participants,
          transactionId: p.transactionId,
          notes: p.notes,
          refundedAmount: p.status === 'refunded' ? p.amount : undefined,
          refundedAt: p.status === 'refunded' ? p.paidAt : undefined
        }));
        
        setPayments(transformedPayments);
      } catch (error) {
        console.error('Error fetching payments:', error);
        // Fallback to mock data if API fails
        setPayments(mockPayments);
      }
    };
    
    fetchPayments();
  }, []);

  useEffect(() => {
    // Filter anwenden
    let filtered = [...payments];

    // Status-Filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }
    // Methoden-Filter
    if (filterMethod !== 'all') {
      filtered = filtered.filter(p => p.method === filterMethod);
    }

    // Suchfilter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.bookingReference.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Datumsfilter
    if (dateRange.start) {
      filtered = filtered.filter(p => 
        new Date(p.dueDate) >= new Date(dateRange.start)
      );
    }
    if (dateRange.end) {
      filtered = filtered.filter(p => 
        new Date(p.dueDate) <= new Date(dateRange.end)
      );
    }

    // Sortierung
    filtered.sort((a, b) => {      const aValue = a[sortConfig.key as keyof Payment];
      const bValue = b[sortConfig.key as keyof Payment];
      
      if (aValue === undefined || bValue === undefined) return 0;
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredPayments(filtered);
  }, [payments, filterStatus, filterMethod, searchTerm, dateRange, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#4CAF50';
      case 'partial': return '#FF9800';
      case 'pending': return '#2196F3';
      case 'refunded': return '#9C27B0';      case 'failed': return '#F44336';
      default: return '#757575';
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'stripe': return '💳';
      case 'paypal': return '🅿️';
      case 'transfer': return '🏦';
      case 'cash': return '💵';
      case 'invoice': return '📄';
      default: return '💰';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportCSV = () => {
    const headers = ['Rechnungsnr', 'Kunde', 'Betrag', 'Status', 'Methode', 'Fällig am', 'Bezahlt am'];
    const rows = filteredPayments.map(p => [
      p.invoiceNumber,
      p.customerName,
      formatCurrency(p.amount),
      p.status,
      p.method,
      formatDate(p.dueDate),
      p.paidAt ? formatDateTime(p.paidAt) : '-'
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');    link.href = URL.createObjectURL(blob);
    link.download = `zahlungen_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleMarkAsPaid = (payment: Payment) => {
    const updatedPayment = {
      ...payment,
      status: 'paid' as const,
      paidAt: new Date().toISOString()
    };
    
    setPayments(prev => 
      prev.map(p => p.id === payment.id ? updatedPayment : p)
    );
    
    if (selectedPayment?.id === payment.id) {
      setSelectedPayment(updatedPayment);
    }
  };

  const handleRefund = (payment: Payment) => {
    const refundAmount = prompt('Rückerstattungsbetrag:', payment.amount.toString());
    if (refundAmount) {
      const updatedPayment = {
        ...payment,
        status: 'refunded' as const,
        refundedAmount: parseFloat(refundAmount),
        refundedAt: new Date().toISOString()
      };
            setPayments(prev => 
        prev.map(p => p.id === payment.id ? updatedPayment : p)
      );
      
      if (selectedPayment?.id === payment.id) {
        setSelectedPayment(updatedPayment);
      }
    }
  };

  return (
    <div className="payment-overview">
      {/* Header mit Statistiken */}
      <div className="payment-header">
        <h2>Zahlungsübersicht</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-label">Gesamtumsatz</div>
              <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-label">Heute</div>
              <div className="stat-value">{formatCurrency(stats.todayRevenue)}</div>
            </div>
          </div>          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-label">Diese Woche</div>
              <div className="stat-value">{formatCurrency(stats.weekRevenue)}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <div className="stat-label">Dieser Monat</div>
              <div className="stat-value">{formatCurrency(stats.monthRevenue)}</div>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-label">Ausstehend</div>
              <div className="stat-value">{stats.pendingPayments}</div>
            </div>
          </div>
          <div className="stat-card danger">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-label">Überfällig</div>
              <div className="stat-value">{stats.overduePayments}</div>
            </div>
          </div>          <div className="stat-card info">
            <div className="stat-icon">↩️</div>
            <div className="stat-content">
              <div className="stat-label">Rückerstattungen</div>
              <div className="stat-value">{formatCurrency(stats.refundedAmount)}</div>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-label">Erfolgsrate</div>
              <div className="stat-value">{stats.successRate.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter und Aktionen */}
      <div className="payment-controls">
        <div className="filter-section">
          <input
            type="text"
            placeholder="Suche nach Kunde, E-Mail, Rechnungsnr..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Status</option>
            <option value="paid">Bezahlt</option>
            <option value="partial">Teilzahlung</option>
            <option value="pending">Ausstehend</option>
            <option value="refunded">Rückerstattet</option>
            <option value="failed">Fehlgeschlagen</option>
          </select>

          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="filter-select"
          >
            <option value="all">Alle Methoden</option>
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
            <option value="transfer">Überweisung</option>
            <option value="cash">Bar</option>
            <option value="invoice">Rechnung</option>
          </select>

          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="date-input"            placeholder="Von"
          />
          
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="date-input"
            placeholder="Bis"
          />
        </div>

        <div className="action-buttons">
          <button onClick={handleExportCSV} className="btn btn-secondary">
            📥 Export CSV
          </button>
          <button onClick={() => setShowManualPayment(true)} className="btn btn-primary">
            ➕ Zahlung erfassen
          </button>
        </div>
      </div>

      {/* Zahlungstabelle */}
      <div className="payment-table-container">
        <table className="payment-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('invoiceNumber')}>
                Rechnungsnr {sortConfig.key === 'invoiceNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>              <th onClick={() => handleSort('customerName')}>
                Kunde {sortConfig.key === 'customerName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('amount')}>
                Betrag {sortConfig.key === 'amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Status</th>
              <th>Methode</th>
              <th onClick={() => handleSort('dueDate')}>
                Fällig am {sortConfig.key === 'dueDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Tour-Datum</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map(payment => {
              const isOverdue = payment.status === 'pending' && new Date(payment.dueDate) < new Date();
              
              return (
                <tr key={payment.id} className={isOverdue ? 'overdue' : ''}>
                  <td>{payment.invoiceNumber}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{payment.customerName}</div>
                      <div className="customer-email">{payment.customerEmail}</div>
                    </div>
                  </td>                  <td>
                    <div className="amount-info">
                      <div className="amount-main">{formatCurrency(payment.amount)}</div>
                      {payment.status === 'partial' && (
                        <div className="amount-detail">
                          Anzahlung: {formatCurrency(payment.deposit || 0)}<br />
                          Offen: {formatCurrency(payment.remainingAmount || 0)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(payment.status) }}
                    >
                      {payment.status === 'paid' && '✅ Bezahlt'}
                      {payment.status === 'partial' && '⏳ Teilzahlung'}
                      {payment.status === 'pending' && '⏰ Ausstehend'}
                      {payment.status === 'refunded' && '↩️ Rückerstattet'}
                      {payment.status === 'failed' && '❌ Fehlgeschlagen'}
                    </span>
                  </td>
                  <td>
                    <span className="method-badge">
                      {getMethodIcon(payment.method)} {payment.method}
                    </span>
                  </td>                  <td className={isOverdue ? 'text-danger' : ''}>
                    {formatDate(payment.dueDate)}
                    {isOverdue && <span className="overdue-icon"> ⚠️</span>}
                  </td>
                  <td>{formatDate(payment.tourDate)}</td>
                  <td>
                    <div className="action-buttons-inline">
                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowDetails(true);
                        }}
                        className="btn-icon"
                        title="Details anzeigen"
                      >
                        👁️
                      </button>
                      {payment.status === 'pending' && (
                        <button
                          onClick={() => handleMarkAsPaid(payment)}
                          className="btn-icon"
                          title="Als bezahlt markieren"
                        >
                          ✅
                        </button>
                      )}
                      {(payment.status === 'paid' || payment.status === 'partial') && (
                        <button
                          onClick={() => handleRefund(payment)}
                          className="btn-icon"                          title="Rückerstatten"
                        >
                          ↩️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredPayments.length === 0 && (
          <div className="no-data">
            <p>Keine Zahlungen gefunden</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedPayment && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Zahlungsdetails</h3>
              <button className="modal-close" onClick={() => setShowDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">                <div className="detail-section">
                  <h4>Rechnungsinformationen</h4>
                  <div className="detail-row">
                    <span className="detail-label">Rechnungsnummer:</span>
                    <span className="detail-value">{selectedPayment.invoiceNumber}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Buchungsreferenz:</span>
                    <span className="detail-value">{selectedPayment.bookingReference}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Betrag:</span>
                    <span className="detail-value">{formatCurrency(selectedPayment.amount)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(selectedPayment.status) }}
                    >
                      {selectedPayment.status}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Kundeninformationen</h4>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedPayment.customerName}</span>                  </div>
                  <div className="detail-row">
                    <span className="detail-label">E-Mail:</span>
                    <span className="detail-value">{selectedPayment.customerEmail}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Tour-Datum:</span>
                    <span className="detail-value">{formatDate(selectedPayment.tourDate)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Teilnehmer:</span>
                    <span className="detail-value">{selectedPayment.participants}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Zahlungsinformationen</h4>
                  <div className="detail-row">
                    <span className="detail-label">Zahlungsmethode:</span>
                    <span className="detail-value">
                      {getMethodIcon(selectedPayment.method)} {selectedPayment.method}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Fällig am:</span>
                    <span className="detail-value">{formatDate(selectedPayment.dueDate)}</span>
                  </div>
                  {selectedPayment.paidAt && (
                    <div className="detail-row">                      <span className="detail-label">Bezahlt am:</span>
                      <span className="detail-value">{formatDateTime(selectedPayment.paidAt)}</span>
                    </div>
                  )}
                  {selectedPayment.transactionId && (
                    <div className="detail-row">
                      <span className="detail-label">Transaktions-ID:</span>
                      <span className="detail-value">{selectedPayment.transactionId}</span>
                    </div>
                  )}
                </div>

                {selectedPayment.notes && (
                  <div className="detail-section full-width">
                    <h4>Notizen</h4>
                    <p className="notes-text">{selectedPayment.notes}</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                {selectedPayment.status === 'pending' && (
                  <button 
                    onClick={() => handleMarkAsPaid(selectedPayment)}
                    className="btn btn-primary"
                  >
                    Als bezahlt markieren
                  </button>
                )}
                {(selectedPayment.status === 'paid' || selectedPayment.status === 'partial') && (                  <button 
                    onClick={() => handleRefund(selectedPayment)}
                    className="btn btn-danger"
                  >
                    Rückerstatten
                  </button>
                )}
                <button 
                  onClick={() => alert('E-Mail senden Funktion folgt...')}
                  className="btn btn-secondary"
                >
                  E-Mail senden
                </button>
                <button 
                  onClick={() => alert('Rechnung drucken Funktion folgt...')}
                  className="btn btn-secondary"
                >
                  Rechnung drucken
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manuelle Zahlung erfassen Modal */}
      {showManualPayment && (
        <div className="modal-overlay" onClick={() => setShowManualPayment(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">              <h3>Manuelle Zahlung erfassen</h3>
              <button className="modal-close" onClick={() => setShowManualPayment(false)}>×</button>
            </div>
            <div className="modal-body">
              <form className="payment-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Buchungsnummer *</label>
                    <input type="text" placeholder="z.B. BK-2025-XXX" required />
                  </div>
                  <div className="form-group">
                    <label>Betrag (€) *</label>
                    <input type="number" step="0.01" placeholder="0.00" required />
                  </div>
                  <div className="form-group">
                    <label>Zahlungsmethode *</label>
                    <select required>
                      <option value="">Bitte wählen</option>
                      <option value="cash">Bar</option>
                      <option value="transfer">Überweisung</option>
                      <option value="stripe">Stripe</option>
                      <option value="paypal">PayPal</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zahlungsdatum *</label>
                    <input type="datetime-local" required />
                  </div>
                  <div className="form-group">                    <label>Transaktions-ID</label>
                    <input type="text" placeholder="Optional" />
                  </div>
                  <div className="form-group">
                    <label>Zahlungstyp *</label>
                    <select required>
                      <option value="">Bitte wählen</option>
                      <option value="full">Vollzahlung</option>
                      <option value="deposit">Anzahlung (20%)</option>
                      <option value="partial">Teilzahlung</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Notizen</label>
                    <textarea 
                      rows={3} 
                      placeholder="Optionale Notizen zur Zahlung..."
                    ></textarea>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowManualPayment(false)} className="btn btn-secondary">
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Zahlung erfassen
                  </button>
                </div>
              </form>            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentOverview;